import { useEffect, useId, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import Modal from "../../modal/Modal";
import FormSelect from "../../shared/FormSelect";
import { buildCreateBody, buildUpdateBody, validatePlanForm } from "./planForm";
import { withFieldError } from "./formErrors";

const STATUS_OPTIONS = [
    { value: "ACTIVE", label: "Active" },
    { value: "INACTIVE", label: "Inactive" },
];

const statusOptionFor = (status) =>
    STATUS_OPTIONS.find((option) => option.value === status) ?? STATUS_OPTIONS[0];

const PlanFormDialog = ({
    mode,
    plan,
    clientOptions,
    defaultClientId,
    saving,
    serverError,
    onClose,
    onSubmit,
}) => {
    const titleId = useId();
    const firstFieldRef = useRef(null);
    const isCreate = mode === "create";
    const clientLabel = plan?.client?.displayName || plan?.client?.clientId || "";
    const initialClient = clientOptions.find((option) => option.value === defaultClientId) ?? null;
    const [values, setValues] = useState(() => ({
        planId: isCreate ? "" : plan?.planId ?? "",
        displayName: isCreate ? "" : plan?.displayName ?? "",
        planName: isCreate ? "" : plan?.planName ?? "",
    }));
    const [errors, setErrors] = useState({});
    const valuesRef = useRef(values);
    valuesRef.current = values;
    const { control, getValues } = useForm({
        defaultValues: {
            clientId: isCreate ? initialClient : null,
            status: statusOptionFor(isCreate ? "ACTIVE" : plan?.status),
        },
    });

    useEffect(() => {
        firstFieldRef.current?.focus();
    }, []);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === "Escape" && !saving) onClose();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [onClose, saving]);

    const setField = (key) => (event) => {
        setValues((current) => ({ ...current, [key]: event.target.value }));
    };

    const planSnapshot = (overrides = {}) => ({
        ...valuesRef.current,
        clientId: isCreate ? getValues("clientId")?.value ?? "" : plan?.client?.clientId ?? "",
        status: getValues("status")?.value,
        ...overrides,
    });

    const planErrors = (nextSnapshot) => {
        const nextErrors = validatePlanForm(nextSnapshot, { requireIdentity: isCreate });
        if (nextSnapshot.status !== "ACTIVE" && nextSnapshot.status !== "INACTIVE") nextErrors.status = "Status is required.";
        return nextErrors;
    };

    const reportFieldError = (name, nextSnapshot) => {
        setErrors((current) => withFieldError(current, name, planErrors(nextSnapshot)[name] || ""));
    };

    const changeText = (name) => (event) => {
        setField(name)(event);
        reportFieldError(name, planSnapshot({ [name]: event.target.value }));
    };

    const clientValue = useWatch({ control, name: "clientId" });
    const statusValue = useWatch({ control, name: "status" });
    const formInvalid = Object.keys(planErrors({
        ...values,
        clientId: isCreate ? clientValue?.value ?? "" : plan?.client?.clientId ?? "",
        status: statusValue?.value,
    })).length > 0;

    const handleSubmit = (event) => {
        event.preventDefault();
        if (saving) return;
        const nextValues = planSnapshot();
        const nextErrors = planErrors(nextValues);
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) return;
        onSubmit(isCreate ? buildCreateBody(nextValues) : buildUpdateBody(nextValues));
    };

    return (
        <Modal isOpen className="modal-content-compact" onClose={saving ? undefined : onClose}>
            <form className="cdes-client-form" onSubmit={handleSubmit} noValidate>
                <p className="title" id={titleId}>
                    {isCreate ? "Add Plan" : "Edit Plan"}
                </p>
                {isCreate ? (
                    <FormSelect
                        name="clientId"
                        label="Client"
                        required
                        control={control}
                        options={clientOptions}
                        errors={errors.clientId ? { clientId: { message: errors.clientId } } : {}}
                        placeholder="Select a client"
                        disabled={saving}
                        onSelectedHandler={(option) => reportFieldError("clientId", planSnapshot({ clientId: option?.value ?? "" }))}
                        onBlurHandler={() => reportFieldError("clientId", planSnapshot())}
                    />
                ) : (
                    <div className="form-input">
                        <label htmlFor="cdes-plan-client">Client</label>
                        <input id="cdes-plan-client" value={clientLabel} disabled readOnly />
                    </div>
                )}
                <div className="form-input">
                    <label htmlFor="cdes-plan-id">
                        Plan ID {isCreate ? <span className="danger">*</span> : null}
                    </label>
                    <input
                        id="cdes-plan-id"
                        ref={isCreate ? firstFieldRef : undefined}
                        value={values.planId}
                        onChange={isCreate ? changeText("planId") : setField("planId")}
                        disabled={!isCreate || saving}
                        readOnly={!isCreate}
                        aria-required={isCreate || undefined}
                        aria-invalid={Boolean(errors.planId) || undefined}
                        aria-describedby="cdes-plan-id-help"
                        autoComplete="off"
                    />
                    <p id="cdes-plan-id-help" className="cdes-client-form__help">
                        System identifier. It cannot be changed later.
                    </p>
                    {errors.planId ? (
                        <p className="danger cdes-client-form__error" role="alert">
                            {errors.planId}
                        </p>
                    ) : null}
                </div>
                <div className="form-input">
                    <label htmlFor="cdes-plan-name">
                        Plan Name <span className="danger">*</span>
                    </label>
                    <input
                        id="cdes-plan-name"
                        ref={isCreate ? undefined : firstFieldRef}
                        value={values.displayName}
                        onChange={changeText("displayName")}
                        disabled={saving}
                        aria-required="true"
                        aria-invalid={Boolean(errors.displayName) || undefined}
                        autoComplete="off"
                    />
                    {errors.displayName ? (
                        <p className="danger cdes-client-form__error" role="alert">
                            {errors.displayName}
                        </p>
                    ) : null}
                </div>
                <div className="form-input">
                    <label htmlFor="cdes-plan-source">Source Plan Name</label>
                    <input
                        id="cdes-plan-source"
                        value={values.planName}
                        onChange={setField("planName")}
                        disabled={saving}
                        autoComplete="off"
                        aria-describedby="cdes-plan-source-help"
                    />
                    <p id="cdes-plan-source-help" className="cdes-client-form__help">
                        {isCreate
                            ? "Optional. Leave blank to omit it."
                            : "Optional. Clear this field to remove the stored source name."}
                    </p>
                </div>
                <FormSelect
                    name="status"
                    label="Status"
                    required
                    control={control}
                    options={STATUS_OPTIONS}
                    errors={errors.status ? { status: { message: errors.status } } : {}}
                    placeholder="Status"
                    disabled={saving}
                    onSelectedHandler={(option) => reportFieldError("status", planSnapshot({ status: option?.value ?? "" }))}
                    onBlurHandler={() => reportFieldError("status", planSnapshot())}
                />
                {serverError ? (
                    <p className="danger cdes-client-form__error" role="alert">
                        {serverError}
                    </p>
                ) : null}
                <div className="cdes-client-form__actions">
                    <button type="button" className="outline-btn" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button type="submit" className="edit-btn" disabled={saving || formInvalid}>
                        {saving ? (isCreate ? "Creating…" : "Saving…") : isCreate ? "Create Plan" : "Save Changes"}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default PlanFormDialog;
